import React from 'react'
import { Alert, Box, Spinner, get } from '@ally/metronome-ui'
import styled from 'styled-components'
import {
  amountFormatter,
  getAccountTypeName,
  getServerTimeShortDate,
} from '../../utils/formatter'
import { NickName } from '../common/NickName/NickName'
import { AccountNumberToggle } from '../common/AccountNumberToggle'
import { AccountOwnersDl } from '../common/DlComponents/AccountOwnersDl'
import { BeneficiariesDl } from '../common/DlComponents/BeneficiariesDl'
import { InterestRateDl } from '../common/DlComponents/InterestRateDl'
import { StandardDl } from '../common/DlComponents/StandardDl'
import { TopCard } from '../common/TopCard'
import { TotalProjectedInterestDl } from '../common/DlComponents/TotalProjectedInterestDl'
import { TotalAllowedRateIncreasesDl } from '../common/DlComponents/TotalAllowedRateIncreasesDl'
import { CurrentRedemptionAmountDl } from '../common/DlComponents/CurrentRedemptionAmountDl'
import { RenewalBonusMessage } from './ActionPrompt/RenewalBonusMessage'
import { RenewalInstruction } from './ActionPrompt/RenewalInstruction'
import { GoPaperlessDl } from '../common/DlComponents/GoPaperlessDl'
import { DetailsCardWrapper } from '../common/DetailsCard'
import {
  AcctDetailsAccordionSummaryPanel,
  AcctDetailsAccordionWrapper,
} from '../common/AcctDetailsAccordion'
import { ClosedAccountLabel } from '../common/ClosedAccountLabel'
import { APY } from './APY'
import { useAccountDetailsContext } from '../../services/AccountDetailsProvider'
import { useProductsContext } from '../../services/ProductsProvider'
import { CurrentBalanceTopCard } from '../common/DlComponents/CurrentBalanceTopCard'
import { InterestAccruedTopCard } from '../common/DlComponents/InterestAccruedTopCard'
import { MaturesOnTopCard } from '../common/DlComponents/MaturesOnTopCard'
import { BalanceInterestAccruedDl } from '../common/DlComponents/BalanceInterestAccruedDl'
import { CdTermDl } from '../common/DlComponents/CdTermDl'
import { DailyInterestAccruedDl } from '../common/DlComponents/DailyInterestAccruedDl'
import { InterestWithheldYtdDl } from '../common/DlComponents/InterestWithheldYtdDl'
import { DisbursementMethodDl } from '../common/DlComponents/DisbursmentMethod'
import { InterestWithheldPriorYrDl } from '../common/DlComponents/InterestWithheldPriorYrDl'
import { LastRenewalDateDl } from '../common/DlComponents/LastRenewalDateDl'
import { DateClosedDl } from '../common/DlComponents/DateClosedDl'
import { PayMyInterestDl } from '../common/DlComponents/PayMyInterestDl'
import { RyrActionPrompt } from './ActionPrompt/RaiseYourRate/RyrActionPrompt'
import { PlanAccountNumber } from '../common/PlanAccountNumber'
import { ViewRatesActionPrompt } from './ActionPrompt/ViewRates/ViewRatesActionPrompt'

const StyledSpinner = styled(Spinner).attrs(props => ({
  size: 'small',
}))`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${get.space('sm')};
`

export const CertificateOfDepositDetailsCard: React.FC = () => {
  const { accountDetails } = useAccountDetailsContext()
  const { selectedProduct, hasProductsError, isProductsLoading } =
    useProductsContext()

  // istanbul ignore next // we already have a test for this scenario in AccountDetailsLoader.tsx
  if (!accountDetails) return <></>

  const accountClosed = accountDetails.accountStatus === 'Closed'

  const topCardItems = [
    <CurrentBalanceTopCard account={accountDetails} />,
    <InterestAccruedTopCard account={accountDetails} />,
    <MaturesOnTopCard account={accountDetails} accountClosed={accountClosed} />,
    <APY />,
  ]

  const drawerItems: Array<React.ReactElement | any> = [
    [
      <BalanceInterestAccruedDl account={accountDetails} />,
      <CdTermDl account={accountDetails} />,
      <InterestRateDl account={accountDetails} />,
      <StandardDl
        title="Interest YTD:"
        definition={amountFormatter.format(accountDetails.interestYtd)}
      />,
      <StandardDl
        title="Interest Prior Year:"
        definition={amountFormatter.format(accountDetails.interestPriorYear)}
      />,
      <DailyInterestAccruedDl
        account={accountDetails}
        accountClosed={accountClosed}
      />,
      <InterestWithheldPriorYrDl account={accountDetails} />,
      <TotalProjectedInterestDl
        account={accountDetails}
        accountClosed={accountClosed}
      />,
      <InterestWithheldYtdDl account={accountDetails} />,
      <CurrentRedemptionAmountDl account={accountDetails} />,
      <TotalAllowedRateIncreasesDl account={accountDetails} />,
      <StandardDl
        title="Account Type:"
        definition={getAccountTypeName({
          type: accountDetails.accountType,
          account: accountDetails,
        })}
      />,
      <PayMyInterestDl
        account={accountDetails}
        accountClosed={accountClosed}
      />,
      <DisbursementMethodDl
        account={accountDetails}
        accountClosed={accountClosed}
      />,
      <LastRenewalDateDl account={accountDetails} />,
      <StandardDl
        title="Date Opened:"
        definition={getServerTimeShortDate(accountDetails.acctOpenDate)}
      />,
      <DateClosedDl account={accountDetails} />,
      <GoPaperlessDl />,
    ],
    [
      <AccountOwnersDl account={accountDetails} />,
      // TO DO: move this logic inside BeneficiariesDl and update logic for other account types using this component
      accountDetails.beneficiaries &&
        accountDetails.beneficiaries.length > 0 &&
        !accountClosed && <BeneficiariesDl account={accountDetails} />,
    ],
  ]

  // These prompts will only be shown if we get a successful response from the products call
  const renderCdPrompts = (): React.ReactElement => (
    <>
      <ViewRatesActionPrompt
        account={accountDetails}
        product={selectedProduct}
      />
      <RyrActionPrompt account={accountDetails} product={selectedProduct} />
      <RenewalBonusMessage account={accountDetails} />
      <RenewalInstruction account={accountDetails} product={selectedProduct} />
    </>
  )

  const renderPromptsAndDrawer = (): React.ReactElement => (
    <>
      <ClosedAccountLabel account={accountDetails} />
      <AcctDetailsAccordionWrapper>
        <AcctDetailsAccordionSummaryPanel accordionContents={drawerItems} />
      </AcctDetailsAccordionWrapper>
    </>
  )
  return (
    <Box
      as="section"
      mb="md"
      data-testid="certificate-of-deposit-account-details"
    >
      <DetailsCardWrapper>
        <TopCard account={accountDetails} detailItems={topCardItems}>
          <NickName account={accountDetails} />
          <AccountNumberToggle account={accountDetails} />
          <PlanAccountNumber account={accountDetails} />
        </TopCard>
        {hasProductsError ? (
          <Box p="m">
            <Alert
              variant="error"
              heading="There was a problem loading your account information. Try again later."
            />
          </Box>
        ) : (
          !isProductsLoading && renderCdPrompts()
        )}
        {isProductsLoading && !hasProductsError ? (
          <StyledSpinner />
        ) : (
          renderPromptsAndDrawer()
        )}
      </DetailsCardWrapper>
    </Box>
  )
}
